import { useState, useCallback, useEffect } from 'react';
import { MedplumClient } from '@medplum/core';
import { Patient, Resource } from '@medplum/fhirtypes';
import { useDebounceSearch } from './useDebounceSearch';
import {
  getPatientsByName,
  getPatientNameSuggestions,
} from '../utils/fhir-helpers';
import { useSubscription } from '@medplum/react';

export interface BaseSearchParams {
  patientName?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  [key: string]: any; // Allow additional search params
}

export type SearchQueryResult =
  | Record<string, string | number | boolean | undefined>
  | string[][];

function toQueryPairs(
  obj: Record<string, string | number | boolean | undefined>
): string[][] {
  return Object.entries(obj)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => [k, String(v)]);
}

interface UseResourceSearchConfig<T extends Resource> {
  medplum: MedplumClient;
  resourceType: T['resourceType'];
  patientId?: string;
  itemsPerPage?: number;
  buildSearchQuery: (
    params: BaseSearchParams,
    page: number,
    itemsPerPage: number
  ) => SearchQueryResult;
  getResourceIdSuggestions: (
    medplum: MedplumClient,
    query: string
  ) => Promise<string[]>;
  needsClientSideFiltering?: (params: BaseSearchParams) => boolean;
  clientSideFilter?: (
    resources: T[],
    params: BaseSearchParams,
    bundle: any
  ) => T[] | Promise<T[]>;
  processBundleData?: (bundle: any) => any; // Optional callback to extract additional data from bundle
}

export interface UseResourceSearchResult<T extends Resource> {
  // Resource data
  resources: T[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalCount: number | undefined; // Total number of items (from bundle.total)
  patients: Map<string, Patient>;
  bundleData: any; // Data extracted from bundle via processBundleData callback

  // Search params
  searchParams: BaseSearchParams;
  updateSearchParams: (params: Partial<BaseSearchParams>) => void;
  clearSearch: (clearedParams: BaseSearchParams) => void;

  // Pagination
  setPage: (page: number) => void;

  // Fetch function
  fetchResources: (customParams?: BaseSearchParams) => Promise<void>;

  // Patient name search
  patientNameQuery: string;
  setPatientNameQuery: (query: string) => void;
  setPatientQuery: (query: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;

  // Resource ID search
  resourceIdQuery: string;
  setResourceIdQuery: (query: string) => void;
  resourceIdOptions: string[];
  resourceIdLoading: boolean;
}

export function useResourceSearch<T extends Resource>(
  config: UseResourceSearchConfig<T>
): UseResourceSearchResult<T> {
  const {
    medplum,
    resourceType,
    patientId,
    itemsPerPage = 20,
    buildSearchQuery,
    getResourceIdSuggestions,
    needsClientSideFiltering,
    clientSideFilter,
    processBundleData,
  } = config;

  // State
  const [resources, setResources] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState<BaseSearchParams>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [patients, setPatients] = useState<Map<string, Patient>>(new Map());
  const [bundleData, setBundleData] = useState<any>(null);

  // Patient name search (debounced)
  const patientSearchFunction = useCallback(
    (query: string) => getPatientsByName(medplum, query),
    [medplum]
  );
  const patientNameSuggestionFunction = useCallback(
    (query: string) => getPatientNameSuggestions(medplum, query),
    [medplum]
  );

  const {
    query: _patientQuery,
    setQuery: setPatientQuery,
    results: _patientSearchResults,
    loading: patientLoading,
  } = useDebounceSearch<Patient>({
    searchFunction: patientSearchFunction,
    debounceDelay: 300,
    minQueryLength: 2,
    onError: (error) => {
      console.error('Error searching patients:', error);
    },
  });

  const {
    query: patientNameQuery,
    setQuery: setPatientNameQuery,
    results: patientNameOptions,
    loading: patientNameLoading,
  } = useDebounceSearch<string>({
    searchFunction: patientNameSuggestionFunction,
    debounceDelay: 300,
    minQueryLength: 2,
    onError: (error) => {
      console.error('Error searching patient names:', error);
    },
  });

  // Resource ID search (debounced)
  const resourceIdSearchFunction = useCallback(
    (query: string) => getResourceIdSuggestions(medplum, query),
    [medplum, getResourceIdSuggestions]
  );

  const {
    query: resourceIdQuery,
    setQuery: setResourceIdQuery,
    results: resourceIdOptions,
    loading: resourceIdLoading,
  } = useDebounceSearch<string>({
    searchFunction: resourceIdSearchFunction,
    debounceDelay: 300,
    minQueryLength: 2,
    onError: (error) => {
      console.error('Error searching resource IDs:', error);
    },
  });

  // Helper to update search params consistently and trigger fetch/pagination reset
  const updateSearchParams = useCallback(
    (newParams: Partial<BaseSearchParams>) => {
      setSearchParams((prev) => ({ ...prev, ...newParams }));
      setPage(1);
      // Note: Does NOT auto-fetch. User must click Search button or the component
      // can call fetchResources explicitly
    },
    []
  );

  // Fetch resources
  const fetchResources = useCallback(
    async (customParams?: BaseSearchParams) => {
      medplum.invalidateSearches(resourceType);
      setLoading(true);
      try {
        const params = customParams || searchParams;

        // Check if we need client-side filtering
        const needsClientFilter = needsClientSideFiltering?.(params) || false;

        // Build the search query — normalise to string[][] for consistent handling
        const rawQuery = buildSearchQuery(params, page, itemsPerPage);
        const searchQuery: string[][] = Array.isArray(rawQuery)
          ? rawQuery
          : toQueryPairs(rawQuery);

        // Request total count from server for accurate pagination
        if (!searchQuery.some(([k]) => k === '_total')) {
          searchQuery.push(['_total', 'accurate']);
        }

        // For client-side filtering, fetch more items to filter from
        if (needsClientFilter) {
          const countIdx = searchQuery.findIndex(([k]) => k === '_count');
          const offsetIdx = searchQuery.findIndex(([k]) => k === '_offset');
          if (countIdx >= 0) {
            searchQuery[countIdx] = ['_count', String(itemsPerPage * 10)];
          } else {
            searchQuery.push(['_count', String(itemsPerPage * 10)]);
          }
          if (offsetIdx >= 0) {
            searchQuery[offsetIdx] = ['_offset', '0'];
          } else {
            searchQuery.push(['_offset', '0']);
          }
        }

        // Add patient filter if patientId is provided.
        // When scoped to a single patient, fetch all records (no pagination)
        // so the timeline can match every order to its invoice.
        if (patientId) {
          const param = resourceType === 'Invoice' ? 'recipient' : 'subject';
          searchQuery.push([param, `Patient/${patientId}`]);
          const countIdx = searchQuery.findIndex(([k]) => k === '_count');
          if (countIdx >= 0) {
            searchQuery[countIdx] = ['_count', '200'];
          } else {
            searchQuery.push(['_count', '200']);
          }
          const offsetIdx = searchQuery.findIndex(([k]) => k === '_offset');
          if (offsetIdx >= 0) {
            searchQuery[offsetIdx] = ['_offset', '0'];
          } else {
            searchQuery.push(['_offset', '0']);
          }
        }

        const bundle = await medplum.search(resourceType, searchQuery);
        // Filter to only include resources that matched the search (not _include/_revinclude results)
        let results = (bundle.entry
          ?.filter((e) => e.search?.mode === 'match')
          .map((e) => e.resource as T) || []) as T[];

        // Extract patients from _include results in the bundle
        const patientMap = new Map<string, Patient>();
        for (const entry of bundle.entry ?? []) {
          if (
            entry.search?.mode === 'include' &&
            entry.resource?.resourceType === 'Patient'
          ) {
            const patient = entry.resource as Patient;
            if (patient.id) {
              patientMap.set(patient.id, patient);
            }
          }
        }
        setPatients(patientMap);

        // Process bundle data if callback is provided (e.g., extract _revinclude data)
        if (processBundleData) {
          const extractedData = processBundleData(bundle);
          setBundleData(extractedData);
        }

        // Apply client-side filtering if needed (bundle is passed for _include/_revinclude access)
        if (needsClientFilter && clientSideFilter) {
          results = await clientSideFilter(results, params, bundle);
        }

        // Handle pagination based on whether we did client-side filtering
        let finalResults = results;
        let calculatedTotalPages = 1;
        let calculatedTotalCount: number | undefined = undefined;

        if (needsClientFilter) {
          // For client-side filtered results, paginate the filtered results
          const startIndex = (page - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          finalResults = results.slice(startIndex, endIndex);
          calculatedTotalCount = results.length;
          calculatedTotalPages = Math.max(
            1,
            Math.ceil(results.length / itemsPerPage)
          );
        } else {
          // For server-side pagination, use server's total if available
          const hasNextLink = bundle.link?.some(
            (link) => link.relation === 'next'
          );

          if (bundle.total !== undefined) {
            calculatedTotalCount = bundle.total;
            calculatedTotalPages = Math.max(
              1,
              Math.ceil(bundle.total / itemsPerPage)
            );
          } else if (hasNextLink) {
            calculatedTotalPages = page + 1;
          } else if (results.length === itemsPerPage) {
            calculatedTotalPages = page + 1;
          } else {
            calculatedTotalPages = page;
          }
          finalResults = results;
        }

        setResources(finalResults);
        setTotalCount(calculatedTotalCount);
        setTotalPages(calculatedTotalPages);
      } catch (error) {
        console.error(`Error fetching ${resourceType}:`, error);
      } finally {
        setLoading(false);
      }
    },
    [
      searchParams,
      page,
      patientId,
      medplum,
      itemsPerPage,
      resourceType,
      buildSearchQuery,
      needsClientSideFiltering,
      clientSideFilter,
      processBundleData,
    ]
  );

  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchParams]);

  useSubscription(
    patientId ? `${resourceType}?${resourceType === 'Invoice' ? 'recipient' : 'subject'}=Patient/${patientId}` : resourceType, // Subscribe to all resources
    (_bundle) => {
      //Any resource created/updated -refresh the list
      fetchResources();
    }
  );

  // Clear search function
  const clearSearch = useCallback(
    (clearedParams: BaseSearchParams) => {
      setSearchParams(clearedParams);
      setResourceIdQuery('');
      setPatientNameQuery('');
      setPage(1);
      fetchResources(clearedParams);
    },
    [fetchResources, setResourceIdQuery, setPatientNameQuery]
  );

  return {
    // Resource data
    resources,
    loading,
    page,
    totalPages,
    totalCount,
    patients,
    bundleData,

    // Search params
    searchParams,
    updateSearchParams,
    clearSearch,

    // Pagination
    setPage,

    // Fetch function
    fetchResources,

    // Patient name search
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,

    // Resource ID search
    resourceIdQuery,
    setResourceIdQuery,
    resourceIdOptions,
    resourceIdLoading,
  };
}
